// Phase 1A Step 34 — Football-tier price card.
// Phase H — wired to the Paystack checkout flow.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { type Currency, PRICES, type PriceTier } from "./landing-data.ts";
import { usePlanForTier } from "../../billing/usePlanForTier.ts";
import { initiatePaystackCheckout } from "../../billing/initiatePaystackCheckout.ts";

// Landing-tier id → paystack_plans.tier_code. Tiers without a mapping
// (or with no seeded plan) route to /pricing as a graceful fallback.
const TIER_ID_TO_PLAN_TIER: Record<string, string | undefined> = {
  sika_pro: "sika_pro",
  pro: "phantom_pro",
  elite: "desk_pro",
};

interface PriceCardProps {
  tier: PriceTier;
  featured: boolean;
  currency: Currency;
}

export function PriceCard({ tier, featured, currency }: PriceCardProps): JSX.Element {
  const sym = currency === "GHS" ? "GH₵" : "₦";
  const price = PRICES[tier.id]![currency];

  return (
    <div
      style={{
        position: "relative",
        background: "var(--surface)",
        border: featured ? "1px solid var(--accent)" : "1px solid var(--border)",
        padding: "36px 32px 32px",
        transform: featured ? "translateY(-12px)" : "none",
        boxShadow: featured ? "0 30px 80px rgba(212, 168, 67, 0.08)" : "none",
      }}
    >
      {featured && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            padding: "8px 0",
            background: "var(--accent)",
            color: "#0A0A0A",
            textAlign: "center",
            transform: "translateY(-100%)",
            fontFamily: "var(--mono)",
            fontSize: 10,
            letterSpacing: "0.18em",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          Most Popular
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 20,
        }}
      >
        <div className="display" style={{ fontSize: 36, color: "var(--text)" }}>
          {tier.name}
        </div>
        <div
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: "0.18em",
            color: featured ? "var(--accent)" : "var(--text-dim)",
            textTransform: "uppercase",
          }}
        >
          {tier.tag}
        </div>
      </div>
      <p
        style={{
          fontSize: 13,
          color: "var(--text-muted)",
          lineHeight: 1.55,
          marginBottom: 28,
          minHeight: 60,
        }}
      >
        {tier.tagline}
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          paddingBottom: 24,
          borderBottom: "1px solid var(--border)",
          marginBottom: 24,
        }}
      >
        <span className="mono" style={{ fontSize: 16, color: "var(--text-muted)" }}>
          {sym}
        </span>
        <span
          className="display"
          style={{
            fontSize: 56,
            color: featured ? "var(--accent)" : "var(--text)",
            lineHeight: 0.9,
            letterSpacing: "-0.005em",
          }}
        >
          {price.toLocaleString()}
        </span>
        <span
          className="mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.14em",
            color: "var(--text-muted)",
            textTransform: "uppercase",
            marginLeft: 6,
          }}
        >
          / month
        </span>
      </div>

      <ul
        style={{
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          marginBottom: 32,
          padding: 0,
        }}
      >
        {tier.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span
              style={{
                fontFamily: "var(--mono)",
                color: "var(--accent)",
                fontSize: 11,
                marginTop: 2,
                flex: "0 0 auto",
              }}
            >
              +
            </span>
            <span style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.55 }}>{f}</span>
          </li>
        ))}
      </ul>

      <ChooseTierButton tier={tier} featured={featured} />
    </div>
  );
}

function ChooseTierButton({
  tier,
  featured,
}: {
  tier: PriceTier;
  featured: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const planTierCode = TIER_ID_TO_PLAN_TIER[tier.id];
  const { plan } = usePlanForTier(planTierCode ?? "__none__");
  const [busy, setBusy] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function onClick(): Promise<void> {
    setNotice(null);
    if (!plan) {
      // No paystack_plans row seeded for this tier yet — surface the
      // "launching soon" line and let the user know.
      setNotice(
        "This tier is launching soon. Sign up for early access and we'll notify you when it goes live.",
      );
      return;
    }
    setBusy(true);
    const result = await initiatePaystackCheckout(plan.plan_code);
    setBusy(false);
    if (result.kind === "redirect") {
      window.location.href = result.authorization_url;
      return;
    }
    if (result.kind === "needs_auth") {
      navigate(`/auth?intent=signup&plan=${encodeURIComponent(plan.plan_code)}`);
      return;
    }
    if (result.kind === "not_configured") {
      setNotice(result.message);
      return;
    }
    setNotice(result.message);
  }

  return (
    <>
      <button
        type="button"
        className={featured ? "btn btn-primary" : "btn btn-ghost"}
        style={{ width: "100%", justifyContent: "center" }}
        disabled={busy}
        onClick={() => {
          void onClick();
        }}
      >
        {busy ? "Redirecting…" : `Choose ${tier.name}`} <span className="btn-arrow">→</span>
      </button>
      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 12,
            fontSize: 11,
            color: "var(--text-muted)",
            background: "rgba(212, 168, 67, 0.06)",
            border: "1px solid var(--border)",
            padding: "8px 10px",
            borderRadius: 6,
          }}
        >
          {notice}
        </div>
      ) : null}
    </>
  );
}
